/**
 * Galley's icon set: 24px grid, 2px stroke, round caps and joins, currentColor.
 * Style follows the DOT/AIGA wayfinding signs (docs/research/06 §2.6): solid, geometric,
 * one weight, no perspective. Icons always sit next to a visible text label; they are
 * aria-hidden unless given a `label`.
 */
import type { ReactElement, SVGProps } from 'react'

const dot = (x: number, y: number) => `M${x} ${y}h.01`

const ICONS = {
  'arrow-left': <path d="M19 12H5M11 6l-6 6 6 6" />,
  'arrow-right': <path d="M5 12h14M13 6l6 6-6 6" />,
  'arrow-up': <path d="M12 19V5M6 11l6-6 6 6" />,
  'arrow-down': <path d="M12 5v14M6 13l6 6 6-6" />,
  'chevron-left': <path d="M15 6l-6 6 6 6" />,
  'chevron-right': <path d="M9 6l6 6-6 6" />,
  'chevron-up': <path d="M6 15l6-6 6 6" />,
  'chevron-down': <path d="M6 9l6 6 6-6" />,
  home: <path d="M3.5 11 12 3.5l8.5 7.5M6 9.5V20h12V9.5M10 20v-5h4v5" />,
  list: <path d={`M9 6h11M9 12h11M9 18h11${dot(4.5, 6)}${dot(4.5, 12)}${dot(4.5, 18)}`} />,
  building: <path d="M4 21V8l8-4.5L20 8v13M2.5 21h19M4 12h16M4 16.5h16M10.5 21v-2.5h3V21" />,
  floors: <path d="M12 3 21 7.5 12 12 3 7.5zM3 12l9 4.5 9-4.5M3 16.5 12 21l9-4.5" />,
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="7" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
    </>
  ),
  sliders: (
    <>
      <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />
      <circle cx="15" cy="6" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="17" cy="18" r="2" />
    </>
  ),
  moon: <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4 6.8 6.8 0 0 0 20 14.5z" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
    </>
  ),
  contrast: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" />
    </>
  ),
  'text-size': <path d="M3 19 8 6l5 13M4.9 14.5h6.2M14 19l3.5-8 3.5 8M15 16.7h5" />,
  check: <path d="M5 12.5 9.5 17 19 7" />,
  x: <path d="M6 6l12 12M18 6 6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10.5" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3M12 15v2" />
    </>
  ),
  unlock: (
    <>
      <rect x="5" y="10.5" width="14" height="10.5" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 7.6-1.7M12 15v2" />
    </>
  ),
  pause: <path d="M9 5v14M15 5v14" />,
  play: <path d="M7 4.5v15l13-7.5z" />,
  stop: <rect x="6" y="6" width="12" height="12" rx="1.5" />,
  flag: <path d="M5 21V4M5 4h12l-2.5 4.5L17 13H5" />,
  star: <path d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" />,
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M9.9 5.8A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a16 16 0 0 1-2.4 3.2M6.6 6.6C4 8.3 2.5 12 2.5 12S6 18.5 12 18.5a9 9 0 0 0 5.4-1.7" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M4 4l16 16" />
    </>
  ),
  print: <path d="M7 8V3h10v5M7 17H4.5v-7A2 2 0 0 1 6.5 8h11a2 2 0 0 1 2 2v7H17M7 14h10v7H7z" />,
  qr: (
    <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2.5v2.5H14zM18.5 18.5H20V20h-1.5zM14 19.5V20M19.5 14h.5" />
  ),
  link: (
    <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3A4.5 4.5 0 0 0 13 4.6l-1 1M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />
  ),
  copy: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </>
  ),
  projector: (
    <>
      <rect x="2.5" y="8" width="19" height="9" rx="2" />
      <circle cx="16" cy="12.5" r="2.5" />
      <path d="M5.5 11.5H9M6 17v2.5M18 17v2.5" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.7a3.5 3.5 0 0 1 0 6.6M18 14.2a6.5 6.5 0 0 1 3.5 5.8" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  door: <path d={`M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17M3 21h18${dot(14.5, 12)}`} />,
  stamp: (
    <path d="M12 3a3 3 0 0 0-2 5.2V12h4V8.2A3 3 0 0 0 12 3zM4.5 16a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v2h-15zM5 21h14" />
  ),
  sparkle: (
    <path d="M11 4c.6 4.2 2.8 6.4 7 7-4.2.6-6.4 2.8-7 7-.6-4.2-2.8-6.4-7-7 4.2-.6 6.4-2.8 7-7zM19 3v3.5M17.2 4.8h3.6" />
  ),
  sprout: (
    <path d="M12 21v-8M12 13C12 9 9 7 4.5 7c0 4 3 6 7.5 6zM12 11c0-3.6 2.6-5.6 7-5.6 0 3.6-2.6 5.6-7 5.6zM7 21h10" />
  ),
  question: (
    <>
      <path d="M12 3.5c5 0 8.5 3.2 8.5 7.2S17 18 12 18a9.8 9.8 0 0 1-2.9-.4L4.5 20l1.3-3.8c-1.5-1.3-2.3-3.2-2.3-5.5 0-4 3.5-7.2 8.5-7.2z" />
      <path d={`M9.9 8.8a2.2 2.2 0 1 1 3 2c-.6.3-.9.8-.9 1.4${dot(12, 14.8)}`} />
    </>
  ),
  'eye-see': (
    <>
      <circle cx="10.5" cy="10.5" r="7" />
      <path d="M15.6 15.6 20.5 20.5M6.5 10.5s1.6-2.6 4-2.6 4 2.6 4 2.6-1.6 2.6-4 2.6-4-2.6-4-2.6z" />
      <circle cx="10.5" cy="10.5" r="0.6" fill="currentColor" />
    </>
  ),
  bulb: (
    <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.1V16h5v-.1c0-.8.4-1.6 1.1-2.1A6 6 0 0 0 12 3z" />
  ),
  headphones: (
    <path d="M4 16v-3a8 8 0 0 1 16 0v3M4 15h3v6H5a1 1 0 0 1-1-1zM20 15h-3v6h2a1 1 0 0 0 1-1z" />
  ),
  film: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 4v16M17 4v16M3 8h4M3 12h4M3 16h4M17 8h4M17 12h4M17 16h4" />
    </>
  ),
  book: (
    <path d="M5 5a2 2 0 0 1 2-2h12v15H7a2 2 0 0 0-2 2zM5 19.5A1.5 1.5 0 0 0 6.5 21H19v-3M9 7.5h6" />
  ),
  frame: <path d="M3.5 3.5h17v17h-17zM7 7h10v10H7zM7 15l3-3 2 2 2.5-3L17 14" />,
  placard: <path d="M5 5.5h14v15H5zM12 3v2.5M8.5 10h7M8.5 13.5h7M8.5 17h4" />,
  zine: <path d="M8 3h9l3 3v12H8zM17 3v3h3M5 6.5V20a1 1 0 0 0 1 1h10M11 10h5M11 13.5h5" />,
  booth: <path d={`M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M8 6h8v6H8zM3 21h18${dot(12, 16)}`} />,
  // The family setup's objects (docs/family-setup.md §4.7).
  pedestal: (
    <path d="M9.5 3.5h5l-.8 2.6c1.8.9 2.4 2.8 1.6 4.9H8.7c-.8-2.1-.2-4 1.6-4.9zM7 11h10v9.5H7zM5 20.5h14" />
  ),
  headset: (
    <path d="M5.5 8h13a2.5 2.5 0 0 1 2.5 2.5v4a2.5 2.5 0 0 1-2.5 2.5H15l-3-2.6L9 17H5.5A2.5 2.5 0 0 1 3 14.5v-4A2.5 2.5 0 0 1 5.5 8zM7 8V5.5h10V8" />
  ),
  lectern: <path d="M4 9.5 12 7l8 2.5-1 3.5-7-2-7 2zM12 11v9.5M8.5 20.5h7" />,
  terminal: <path d="M3.5 4.5h17v11.5h-17zM3.5 8h17M9 20.5h6M12 16v4.5" />,
  bell: <path d="M6 16v-5a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20.5a2 2 0 0 0 4 0M12 3v2" />,
  undo: <path d="M9 14 4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />,
  trash: <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M9 7V4h6v3" />,
  refresh: (
    <path d="M20 11a8 8 0 0 0-14.6-4.5L4 8M4 3.5V8h4.5M4 13a8 8 0 0 0 14.6 4.5L20 16M20 20.5V16h-4.5" />
  ),
  external: (
    <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d={`M12 11v6${dot(12, 7.5)}`} />
    </>
  ),
  warning: <path d={`M12 3.5 2.5 20h19zM12 10v4.5${dot(12, 17.2)}`} />,
  google: <path d="M18.5 7.4A8 8 0 1 0 20 12h-7.5" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
    </>
  ),
  keyboard: (
    <>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path
        d={`${dot(6, 10)}${dot(10, 10)}${dot(14, 10)}${dot(18, 10)}${dot(6, 14)}${dot(18, 14)}M9 14h6`}
      />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d={`M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5v.7${dot(12, 17)}`} />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  archive: <path d="M3 4h18v4H3zM5 8v12h14V8M10 12h4" />,
  edit: <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16zM13.5 6.5l4 4" />,
  download: <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />,
  ticket: (
    <path d="M3.5 6h17v4a2 2 0 0 0 0 4v4h-17v-4a2 2 0 0 0 0-4zM14.5 6v2M14.5 11v2M14.5 16v2" />
  ),
  logout: <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 8l-4 4 4 4M6 12h10" />,
  send: <path d="M21 3 10 14M21 3l-6.5 18-3.5-7-7-3.5z" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  // Family museum (visitor screens)
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 5 5" />
    </>
  ),
  upload: <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />,
  camera: (
    <>
      <path d="M3 8h4l2-3h6l2 3h4v11H3z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  map: <path d="M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20zM9 4v13.5M15 6.5V20" />,
} satisfies Record<string, ReactElement>

export type IconName = keyof typeof ICONS
export const ICON_NAMES = Object.keys(ICONS) as IconName[]

export function isIconName(value: string): value is IconName {
  return Object.hasOwn(ICONS, value)
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName
  /** Rendered size in px (square). */
  size?: number
  /** Only for icons that stand alone and carry meaning; otherwise the icon is hidden. */
  label?: string
}

export function Icon({ name, size = 24, label, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      {...rest}
    >
      {ICONS[name]}
    </svg>
  )
}
